import { useState, useCallback, useEffect, useRef } from 'react'
import { useDropzone, type FileRejection } from 'react-dropzone'
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  IconButton,
  ListItemText,
  Alert,
  CircularProgress,
  TextField,
  Button,
} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import AddLinkIcon from '@mui/icons-material/AddLink'
import { COLORS } from '../theme'
import { outlinedButtonSx, textFieldSx, inputSx } from '../styles'
import {
  ALLOWED_FILE_TYPES_LABEL,
  describeRejectionReason,
} from '@/utils/uploadFileTypes'
import { isBlockedUrl, getBlockedUrlMessage } from '../utils/urlValidation'

export interface FileItem {
  uid: string
  name: string
  status: 'uploading' | 'success' | 'error'
  type: 'file' | 'url'
  docId?: string
}

interface UploadComponentProps {
  /** Called when user drops/selects files. Parent handles the actual upload. */
  onUploadFiles: (files: File[]) => Promise<Array<{ id: string }>>
  /** Called when user clicks delete on a file. Parent handles the API call. */
  onDeleteFile: (docId: string) => Promise<void>
  /** Called when user submits a URL. If not provided, URL input is hidden. */
  onUploadUrl?: (url: string) => Promise<{ id?: string }>
  /** Called when uploading state changes (true = uploading, false = idle). */
  onUploadingChange?: (isUploading: boolean) => void
  /** Existing server files to display */
  existingFiles?: FileItem[]
  maxFiles?: number
  maxSize?: number
  /** When true, the drop zone expands to fill available vertical space */
  fillHeight?: boolean
}

export default function UploadComponent({
  onUploadFiles,
  onDeleteFile,
  onUploadUrl,
  onUploadingChange,
  existingFiles = [],
  maxFiles = 5,
  maxSize = 26214400,
  fillHeight = false,
}: UploadComponentProps) {
  const [files, setFiles] = useState<FileItem[]>(existingFiles)
  const deletedUidsRef = useRef(new Set<string>())

  useEffect(() => {
    if (existingFiles.length > 0) {
      setFiles((prev) => {
        const existingUids = new Set(prev.map((f) => f.uid))
        const newFromServer = existingFiles.filter(
          (f) => !existingUids.has(f.uid) && !deletedUidsRef.current.has(f.uid),
        )
        return newFromServer.length > 0 ? [...prev, ...newFromServer] : prev
      })
    }
  }, [existingFiles])

  const isUploading = files.some((f) => f.status === 'uploading')
  useEffect(() => {
    onUploadingChange?.(isUploading)
  }, [isUploading, onUploadingChange])

  const [error, setError] = useState<string | null>(null)
  const [urlInput, setUrlInput] = useState('')
  const [urlError, setUrlError] = useState<string | null>(null)

  const uploadFiles = useCallback(
    async (acceptedFiles: File[]) => {
      const items: FileItem[] = acceptedFiles.map((file) => ({
        uid: crypto.randomUUID(),
        name: file.name,
        status: 'uploading' as const,
        type: 'file' as const,
      }))
      setFiles((prev) => [...prev, ...items])

      try {
        const results = await onUploadFiles(acceptedFiles)
        setFiles((prev) =>
          prev.map((f) => {
            const idx = items.findIndex((item) => item.uid === f.uid)
            if (idx === -1) return f
            const result = results[idx]
            return result
              ? {
                  ...f,
                  uid: `server-${result.id}`,
                  status: 'success' as const,
                  docId: result.id,
                }
              : { ...f, status: 'error' as const }
          }),
        )
      } catch {
        setFiles((prev) =>
          prev.map((f) =>
            items.some((item) => item.uid === f.uid)
              ? { ...f, status: 'error' as const }
              : f,
          ),
        )
      }
    },
    [onUploadFiles],
  )

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      const currentCount = files.filter((f) => f.status !== 'error').length
      if (currentCount + acceptedFiles.length > maxFiles) {
        setError(`Maximum ${maxFiles} files allowed`)
        return
      }

      const validFiles = acceptedFiles.filter((file) => {
        if (file.size > maxSize) {
          setError(
            `${file.name} exceeds maximum size of ${Math.round(maxSize / 1048576)}MB`,
          )
          return false
        }
        return true
      })

      if (rejectedFiles.length > 0) {
        setError(
          `${rejectedFiles.map((r) => r.file.name).join(', ')} - ${describeRejectionReason(rejectedFiles[0])} Allowed: ${ALLOWED_FILE_TYPES_LABEL}.`,
        )
      } else if (validFiles.length === acceptedFiles.length) {
        setError(null)
      }

      if (validFiles.length > 0) {
        uploadFiles(validFiles)
      }
    },
    [files, maxFiles, maxSize, uploadFiles],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
      'text/csv': ['.csv'],
    },
  })

  const handleAddUrl = useCallback(async () => {
    if (!urlInput.trim() || !onUploadUrl) return

    if (isBlockedUrl(urlInput.trim())) {
      setUrlError(getBlockedUrlMessage())
      return
    }

    setUrlError(null)
    const uid = crypto.randomUUID()
    const urlItem: FileItem = {
      uid,
      name: urlInput.trim(),
      status: 'uploading',
      type: 'url',
    }
    setFiles((prev) => [...prev, urlItem])
    setUrlInput('')

    try {
      const result = await onUploadUrl(urlInput.trim())
      setFiles((prev) =>
        prev.map((f) =>
          f.uid === uid
            ? {
                ...f,
                status: 'success',
                docId: result.id ? String(result.id) : undefined,
              }
            : f,
        ),
      )
    } catch {
      setFiles((prev) =>
        prev.map((f) => (f.uid === uid ? { ...f, status: 'error' } : f)),
      )
    }
  }, [urlInput, onUploadUrl])

  const handleRemove = useCallback(
    async (uid: string) => {
      const file = files.find((f) => f.uid === uid)
      if (!file) return

      // Track deletion so the existingFiles merge won't re-add it
      deletedUidsRef.current.add(uid)
      setFiles((prev) => prev.filter((f) => f.uid !== uid))

      // Best-effort server delete — if it fails, the query refetch
      // will restore the file to the list via existingFiles merge
      if (file.docId) {
        try {
          await onDeleteFile(file.docId)
        } catch {
          // Silently handled — existingFiles will reconcile
        }
      }
    },
    [files, onDeleteFile],
  )

  return (
    <Box
      sx={
        fillHeight ? { display: 'flex', flexDirection: 'column', flex: 1 } : {}
      }
    >
      <Paper
        {...getRootProps()}
        sx={{
          p: 4,
          textAlign: 'center',
          border: `2px dashed ${COLORS.disabledIcon}`,
          borderRadius: 2,
          backgroundColor: isDragActive
            ? COLORS.uploadDragActiveBg
            : COLORS.hoverBg,
          cursor: 'pointer',
          boxShadow: 'none',
          transition: 'background-color 0.15s ease',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          ...(fillHeight ? { flex: 1, minHeight: 120 } : {}),
        }}
      >
        <input {...getInputProps()} />
        <UploadFileIcon sx={{ fontSize: 40, color: COLORS.link, mb: 1 }} />
        <Typography variant="body1" color="textSecondary">
          Drag files here or click to upload
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Max file size: {Math.round(maxSize / 1048576)}MB. Max files per
          upload: {maxFiles}. Allowed: {ALLOWED_FILE_TYPES_LABEL}
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {isUploading && (
        <Box
          role="progressbar"
          aria-valuenow={files.filter((f) => f.status === 'success').length}
          aria-valuemin={0}
          aria-valuemax={
            files.filter(
              (f) => f.status === 'uploading' || f.status === 'success',
            ).length
          }
          aria-label="File upload progress"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: -1 }}
        >
          <CircularProgress
            aria-hidden="true"
            size={14}
            sx={{ color: COLORS.primary }}
          />
          <Typography variant="caption" sx={{ color: COLORS.secondary }}>
            Uploading {files.filter((f) => f.status === 'success').length} of{' '}
            {
              files.filter(
                (f) => f.status === 'uploading' || f.status === 'success',
              ).length
            }{' '}
            files...
          </Typography>
        </Box>
      )}

      {files.length > 0 && (
        <List sx={{ mt: 2 }}>
          {files.map((file) => (
            <ListItem
              key={file.uid}
              secondaryAction={
                <IconButton
                  edge="end"
                  onClick={() => handleRemove(file.uid)}
                  aria-label={`Remove ${file.name}`}
                  size="small"
                >
                  <DeleteOutlineIcon />
                </IconButton>
              }
              sx={{
                borderBottom: `1px solid ${COLORS.border}`,
                py: 1,
              }}
            >
              <Box
                sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}
                aria-label={
                  file.status === 'uploading'
                    ? 'Uploading'
                    : file.status === 'success'
                      ? 'Uploaded successfully'
                      : 'Upload failed'
                }
              >
                {file.status === 'uploading' && (
                  <CircularProgress
                    aria-hidden="true"
                    size={18}
                    sx={{ color: COLORS.primary }}
                  />
                )}
                {file.status === 'success' && (
                  <CheckCircleOutlineIcon
                    aria-hidden="true"
                    sx={{ color: COLORS.success, fontSize: 20 }}
                  />
                )}
                {file.status === 'error' && (
                  <ErrorOutlineIcon
                    aria-hidden="true"
                    sx={{ color: COLORS.error, fontSize: 20 }}
                  />
                )}
              </Box>
              <ListItemText
                primary={file.name}
                secondary={
                  file.status === 'error'
                    ? 'Upload failed — please try again'
                    : file.type === 'url'
                      ? 'URL'
                      : undefined
                }
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          ))}
        </List>
      )}

      {onUploadUrl && (
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Add a URL"
            placeholder="https://..."
            value={urlInput}
            onChange={(e) => {
              setUrlInput(e.target.value)
              setUrlError(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddUrl()
            }}
            variant="outlined"
            sx={textFieldSx}
            slotProps={{ input: { sx: inputSx } }}
          />
          <Button
            disableRipple
            variant="outlined"
            onClick={handleAddUrl}
            disabled={!urlInput.trim()}
            aria-label="Add URL"
            sx={{
              ...outlinedButtonSx,
              minWidth: 'auto',
            }}
          >
            <AddLinkIcon aria-hidden="true" />
          </Button>
        </Box>
      )}

      {urlError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {urlError}
        </Alert>
      )}
    </Box>
  )
}
